import { ATTRIBUTION_KEYS, safeAttribution, safeDiagnostic, safeUrl, type ClientEventName } from '../../../supabase/functions/_shared/activity.ts'
import { browserAnalyticsExcluded } from './analyticsPreference'
const STORAGE = 'arctic-hire:activity-v1'
const TIMEOUT = 30 * 60_000
const MEASUREMENT_ID = 'G-3XGPEN9EMP'
// First-party milestones also support admin reports. Only these events go to GA.
const GOOGLE_EVENTS: Partial<Record<ClientEventName, string>> = {
    booking_landed: 'booking_landed',
    postcode_entered: 'postcode_entered',
    addons_viewed: 'addons_viewed',
    addons_changed: 'addons_changed',
    details_viewed: 'delivery_payment_started',
    details_completed: 'details_completed',
    confirmation_viewed: 'confirmation_viewed',
    validation_failed: 'validation_failed',
    payment_failed: 'payment_failed',
    trustpilot_navigation: 'trustpilot_navigation',
    hire_steps_viewed: 'hire_steps_viewed',
}
type Visit = Record<string, string> & { id: string; last: string }
type Activity = { id: string; name: ClientEventName; at: string; stage: string; product_type: string; sku: string; release: string; properties: Record<string, unknown> }
type Gtag = (...args: unknown[]) => void
let visit: Visit | null = null
let endpoint = ''
let stage = 'dates'
let product = { type: '', sku: '', name: '' }
let queue: Array<{ visit: Visit; event: Activity; tries: number }> = []
let sending = false
let installed = false
let presenceInstalled = false
let presenceEndpoint = ''
let timer: ReturnType<typeof setTimeout> | undefined
let seen = new Set<string>()
const browser = () => typeof window !== 'undefined'
const ga = () => (window as unknown as { gtag?: Gtag }).gtag
function persist() { try { sessionStorage.setItem(STORAGE, JSON.stringify({ visit, seen: [...seen].slice(-100) })) } catch { /* private browsing */ } }
function ensureVisit(): Visit {
    if (!visit) {
        try {
            const saved = JSON.parse(sessionStorage.getItem(STORAGE) || 'null')
            if (saved?.visit?.id && Date.now() - Number(saved.visit.last) < TIMEOUT) { visit = saved.visit; seen = new Set(saved.seen || []) }
        } catch { /* storage is optional */ }
    }
    if (!visit || Date.now() - Number(visit.last) >= TIMEOUT) {
        const query = new URLSearchParams(window.location.search)
        const attribution = Object.fromEntries(ATTRIBUTION_KEYS.map(key => [key, query.get(key) || '']))
        visit = { id: crypto.randomUUID(), last: String(Date.now()), entry_type: query.get('type')?.toLowerCase() || '', entry_sku: query.get('sku')?.toLowerCase() || '', device: window.innerWidth < 700 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop', landing_page: safeUrl(window.location.href), referrer: safeUrl(document.referrer), ...safeAttribution(attribution) }
        seen = new Set()
    }
    return visit
}
function refreshGa() {
    if (!browser() || !ga()) return
    const target = ensureVisit()
    for (const field of ['client_id', 'session_id']) ga()?.('get', MEASUREMENT_ID, field, (value: unknown) => {
        if (/^\d{1,20}(?:\.\d{1,20})?$/.test(String(value)) && visit?.id === target.id) { visit[`ga_${field}`] = String(value); persist() }
    })
}
export function activityAttribution(variant = 'standalone-booking'): Record<string, string> {
    if (!browser()) return {}
    refreshGa()
    const v = ensureVisit()
    return { ...safeAttribution(v), visit_id: v.id, analytics_variant: variant }
}
export function activityVisit() { return browser() ? { ...ensureVisit() } : undefined }
export function activityHeaders(): Record<string, string> { return browser() ? { 'X-Visit-ID': ensureVisit().id, ...(browserAnalyticsExcluded() ? { 'X-Analytics-Excluded': 'true' } : {}) } : {} }
export function setActivityContext(next: { stage?: string; type?: string; sku?: string; name?: string }) {
    if (next.stage) stage = next.stage
    product = { ...product, ...(next.type ? { type: next.type } : {}), ...(next.sku ? { sku: next.sku } : {}), ...(next.name ? { name: next.name } : {}) }
}
function sendGoogleEvent(name: string, properties: Record<string, unknown> = {}) {
    if (!browser() || window.location.hostname !== 'book.arctic-hire.co.uk') return
    try {
        const params: Record<string, unknown> = { product_type: product.type, sku: product.sku, stage, ...properties, page_location: safeUrl(window.location.href), page_referrer: safeUrl(document.referrer) }
        delete params.message; delete params.stack; delete params.request_id
        // Selection details belong only to the first-party journey timeline.
        delete params.postcode; delete params.delivery_date; delete params.collection_date
        ga()?.('event', name, params)
    } catch { /* Google tracking must never interrupt checkout. */ }
}
export function trackPaymentStarted(paymentType: string) {
    sendGoogleEvent('payment_started', { payment_type: ['card', 'apple_pay', 'google_pay'].includes(paymentType) ? paymentType : 'wallet' })
}
export function trackDatesSubmitted() {
    sendGoogleEvent('dates_submitted')
}
export function trackActivity(name: ClientEventName, properties: Record<string, unknown> = {}, once = false) {
    if (!browser() || !endpoint) return
    try {
        const v = ensureVisit()
        const identity = `${name}:${product.type}:${product.sku}`
        v.last = String(Date.now()); persist()
        if (once && seen.has(identity)) return
        if (once) seen.add(identity)
        persist()
        queue.push({ visit: { ...v }, tries: 0, event: { id: crypto.randomUUID(), name, at: new Date().toISOString(), stage, product_type: product.type, sku: product.sku, release: import.meta.env.VITE_RELEASE_ID || (import.meta.env.PROD ? 'production-unversioned' : 'development'), properties } })
        queue = queue.slice(-100)
        if (!timer) timer = setTimeout(() => { timer = undefined; void flushActivity() }, 800)
        const googleEvent = GOOGLE_EVENTS[name]
        if (googleEvent) sendGoogleEvent(googleEvent, properties)
    } catch { /* activity is never a booking dependency */ }
}
export async function flushActivity(final = false) {
    if (!endpoint || sending || !queue.length) return
    sending = true
    const first = queue[0]
    const batch = queue.filter(item => item.visit.id === first.visit.id).slice(0, 20)
    const ids = new Set(batch.map(item => item.event.id))
    const current = visit?.id === first.visit.id ? visit : first.visit
    try {
        const response = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', ...(browserAnalyticsExcluded() ? { 'X-Analytics-Excluded': 'true' } : {}) }, body: JSON.stringify({ visit: current, events: batch.map(item => item.event) }), keepalive: final, signal: AbortSignal.timeout(5000) })
        if (response.ok || response.status === 400 || response.status === 413) queue = queue.filter(item => !ids.has(item.event.id))
        else throw new Error('activity_unavailable')
    } catch {
        batch.forEach(item => item.tries++)
        queue = queue.filter(item => item.tries < 3)
    } finally {
        sending = false
        if (queue.length && !final && !timer) timer = setTimeout(() => { timer = undefined; void flushActivity() }, 5000)
    }
}
type RequestContext = { endpoint: string; method: string; attempts: number; status?: number; online?: boolean }
export function reportActivityError(code: string, error?: unknown, requestId?: string, request?: RequestContext) {
    const e = error instanceof Error ? error : undefined
    const context = request ? `${request.method} ${request.endpoint}; attempts=${request.attempts}; status=${request.status ?? 'no-response'}; online=${request.online ?? 'unknown'}. ` : ''
    trackActivity('client_error', { code, ...(requestId ? { request_id: requestId } : {}), message: safeDiagnostic(`${context}${e ? `${e.name}: ${e.message}` : code}`, 300), stack: e ? safeDiagnostic(e.stack) : '' })
}
// Presence is opt-in for the standalone booking app, not embedded main-site forms.
function initializePresence(apiBaseUrl: string) {
    presenceEndpoint = `${apiBaseUrl.replace(/\/$/, '')}/v1/presence`
    if (presenceInstalled) return
    presenceInstalled = true
    let interval: ReturnType<typeof setInterval> | undefined
    let request: AbortController | undefined
    let resumePending = false
    let pageShown = true
    const visible = () => pageShown && document.visibilityState === 'visible'
    const heartbeat = async () => {
        if (!visible()) return
        if (request) { resumePending = true; return }
        const controller = new AbortController()
        request = controller
        const timeout = setTimeout(() => controller.abort(), 5000)
        try {
            const current = ensureVisit()
            current.last = String(Date.now()); persist()
            await fetch(presenceEndpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', ...(browserAnalyticsExcluded() ? { 'X-Analytics-Excluded': 'true' } : {}) }, body: JSON.stringify({ visit_id: current.id }), signal: controller.signal })
        } catch { /* Presence is optional; the next heartbeat recovers without a queue. */ }
        finally {
            clearTimeout(timeout)
            request = undefined
            if (resumePending) { resumePending = false; void heartbeat() }
        }
    }
    const sync = () => {
        clearInterval(interval)
        interval = undefined
        resumePending = false
        if (!visible()) { request?.abort(); return }
        void heartbeat()
        interval = setInterval(() => void heartbeat(), 30_000)
    }
    document.addEventListener('visibilitychange', sync)
    window.addEventListener('pagehide', () => { pageShown = false; sync() })
    window.addEventListener('pageshow', () => { if (!pageShown) { pageShown = true; sync() } })
    sync()
}
export function initializeActivity(apiBaseUrl: string, options: { presence?: boolean } = {}) {
    if (!browser()) return
    endpoint = `${apiBaseUrl.replace(/\/$/, '')}/v1/activity`
    if (options.presence) initializePresence(apiBaseUrl)
    if (installed) return
    installed = true
    try { ensureVisit(); refreshGa() } catch { return }
    if (window.location.pathname !== '/booking-confirmation') trackActivity('booking_landed', {}, true)
    ga()?.('event', 'page_view', { page_location: safeUrl(window.location.href), page_referrer: safeUrl(document.referrer) })
    window.addEventListener('error', event => reportActivityError('UNCAUGHT_ERROR', event.error instanceof Error ? event.error : new Error('Script resource failed')))
    window.addEventListener('unhandledrejection', event => { if (event.reason?.name !== 'AbortError') reportActivityError('UNHANDLED_REJECTION', event.reason) })
    let lastHeartbeat = Date.now()
    let lastTouch = 0
    const touch = () => {
        try {
            if (Date.now() - lastTouch < 5000) return
            lastTouch = Date.now()
            ensureVisit().last = String(Date.now()); persist()
            if (Date.now() - lastHeartbeat > 60_000) { lastHeartbeat = Date.now(); trackActivity('visit_active') }
        } catch { /* optional activity only */ }
    }
    window.addEventListener('pointerdown', touch, { passive: true })
    window.addEventListener('keydown', touch, { passive: true })
    window.addEventListener('scroll', touch, { passive: true })
    window.addEventListener('pagehide', () => void flushActivity(true))
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') void flushActivity(true) })
}
